import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  ThinDropdown,
  ThinTextField,
  AddButton,
  ThinDropdownWithSearch,
  BackButton,
} from "../../common/GeneralComponent";
import { fetchData } from "../../../utils/functions";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useParams } from "react-router-dom";
import Loader from "../../../components/partials/Loader";
import { useDispatch } from "react-redux";

const EditUnion = () => {
  const { id } = useParams();
  const [districts, setDistricts] = useState([]);
  const [filteredDistricts, setFilteredDistricts] = useState([]);
  const [tehsils, setTehsils] = useState([]);
  const [filteredTehsils, setFilteredTehsils] = useState([]);
  const [loader, setLoader] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // ✅ Fetch UC details + preload districts/tehsils
  useEffect(() => {
    const fetchDataForEdit = async () => {
      setLoader(true);
      try {
        const ucRes = await fetchData(
          "all-ucs",
          "GET",
          null,
          {},
          { uc_id: id, limit: 1, page: 1 },
          navigate,
          "/ucs",
          dispatch
        );

        const ucData = ucRes?.data?.[0];
        if (!ucData) {
          toast.error("UC not found");
          navigate("/ucs");
          return;
        }

        const ucId = ucData.uc_id || ucData.UC_Id;
        const ucName = ucData.uc_name || ucData.UC_Name;
        const districtId = ucData.district_id || ucData.District_Id;
        const tehsilId = ucData.tehsil_id || ucData.Tehsil_Id;
        const status =
          ucData.status !== undefined
            ? ucData.status
            : ucData.Status !== undefined
            ? ucData.Status
            : 1;

        // preload districts
        const districtsRes = await fetchData(
          "districts",
          "GET",
          null,
          {},
          {},
          navigate,
          "/districts",
          dispatch
        );
        const allDists = districtsRes?.data || [];
        setDistricts(allDists);
        setFilteredDistricts(allDists.filter((d) => d.status === 1));

        // preload tehsils for this district
        if (districtId) {
          await fetchTehsils(districtId);
        }

        formik.setValues({
          UC_Id: ucId,
          UC_Name: ucName,
          District_Id: districtId || "",
          Tehsil_Id: tehsilId || "",
          Status: status,
        });
      } catch (error) {
        console.error("Error fetching UC:", error);
        toast.error("Failed to load UC data");
        navigate("/ucs");
      } finally {
        setLoader(false);
      }
    };

    if (id) fetchDataForEdit();
  }, [id, navigate, dispatch]);

  // ✅ Fetch Tehsils by District
  const fetchTehsils = async (districtId) => {
    if (!districtId) {
      setFilteredTehsils([]);
      return;
    }
    setLoader(true);
    try {
      const response = await fetchData(
        "tehsils",
        "GET",
        null,
        {},
        { district_id: districtId }, // filter by district
        navigate,
        "/tehsils",
        dispatch
      );
      const allTehsils = response?.data || [];
      setTehsils(allTehsils);
      setFilteredTehsils(allTehsils.filter((t) => t.status === 1));
    } catch (error) {
      toast.error("Failed to load tehsils");
    } finally {
      setLoader(false);
    }
  };

  // ✅ handle district change
  const handleDistrictChange = (selectedDistrict) => {
    formik.setValues((prev) => ({
      ...prev,
      District_Id: selectedDistrict,
      Tehsil_Id: "",
    }));
    fetchTehsils(selectedDistrict);
  };

  // ✅ Status change check
  const handleStatusChange = (selectedStatus) => {
    if (selectedStatus == 1) {
      if (formik.values.District_Id) {
        const selectedDistrict = districts.find(
          (d) =>
            d.district_id == formik.values.District_Id ||
            d.District_Id == formik.values.District_Id
        );
        if (selectedDistrict && selectedDistrict.status === 0) {
          toast.error(
            "Cannot activate union. First activate related District."
          );
          return;
        }
      }

      if (formik.values.Tehsil_Id) {
        const selectedTehsil = tehsils.find(
          (t) =>
            t.tehsil_id == formik.values.Tehsil_Id ||
            t.Tehsil_Id == formik.values.Tehsil_Id
        );
        if (selectedTehsil && selectedTehsil.status === 0) {
          toast.error("Cannot activate union. First activate related Tehsil.");
          return;
        }
      }
    }
    formik.setFieldValue("Status", selectedStatus);
  };

  const validationSchema = Yup.object({
    UC_Name: Yup.string()
      .max(50, "Union Name cannot exceed 50 characters")
      .required("Union Name is required"),
    District_Id: Yup.string().required("District is required"),
    Tehsil_Id: Yup.string().required("Tehsil is required"),
    Status: Yup.number().required("Status is required"),
  });

  const formik = useFormik({
    initialValues: {
      UC_Id: id,
      UC_Name: "",
      District_Id: "",
      Tehsil_Id: "",
      Status: 1,
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        const data = {
          uc_id: values.UC_Id,
          uc_name: values.UC_Name,
          district_id: values.District_Id,
          tehsil_id: values.Tehsil_Id,
          status: values.Status,
        };

        const response = await fetchData(
          "ucs",
          "PUT",
          data,
          {},
          {},
          navigate,
          "/ucs",
          dispatch
        );
        toast.success(response.message || "UC updated successfully");
        navigate("/ucs");
      } catch (error) {
        console.error("Error updating UC:", error);
        toast.error(error.message || "Failed to update UC");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  if (loader) return <Loader />;

  return (
    <div>
      <BackButton text="Back" to="/ucs" />
      <h1 className="text-black text-[1.475rem] font-[Bold] ">
        Edit Union Council
      </h1>
      <div className="bg-white rounded-lg shadow w-full my-5">
        <div className="p-4 flex md:flex-row rounded-t-lg gradiant2 flex-col gap-3 justify-between md:items-center border-lightGray border-b mb-4">
          <h1 className="lg:text-[0.875rem] text-[13px] tracking-[0.0625rem] text-[#856800] font-[bold] uppercase">
            Edit Union Council
          </h1>
        </div>
        <form onSubmit={formik.handleSubmit}>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 p-4 my-4">
            <ThinDropdownWithSearch
              label="District"
              id="district-dropdown"
              options={filteredDistricts.map((district) => ({
                value: district.District_Id || district.district_id,
                label: district.District_Name || district.district_name,
              }))}
              value={formik.values.District_Id}
              onChange={handleDistrictChange} // ✅ dependency
              error={formik.touched.District_Id && formik.errors.District_Id}
              helperText={
                formik.touched.District_Id && formik.errors.District_Id
              }
            />

            <ThinDropdownWithSearch
              label="Tehsil"
              id="tehsil-dropdown"
              options={filteredTehsils.map((tehsil) => ({
                value: tehsil.Tehsil_Id || tehsil.tehsil_id,
                label: tehsil.Tehsil_Name || tehsil.tehsil_name,
              }))}
              value={formik.values.Tehsil_Id}
              onChange={(value) => formik.setFieldValue("Tehsil_Id", value)}
              disabled={!formik.values.District_Id}
              error={formik.touched.Tehsil_Id && formik.errors.Tehsil_Id}
              helperText={formik.touched.Tehsil_Id && formik.errors.Tehsil_Id}
            />

            <ThinTextField
              id="UC_Name"
              name="UC_Name"
              label="Union Name"
              placeholder="Enter Union Name"
              type="text"
              value={formik.values.UC_Name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.UC_Name && formik.errors.UC_Name}
              helperText={formik.touched.UC_Name && formik.errors.UC_Name}
              maxLength={50}
            />

            <ThinDropdown
              label="Status"
              id="status-dropdown"
              name="Status"
              options={[
                { value: 1, label: "Active" },
                { value: 0, label: "In-Active" },
              ]}
              value={formik.values.Status}
              onChange={(e) => handleStatusChange(parseInt(e.target.value))}
              error={formik.touched.Status && formik.errors.Status}
              helperText={formik.touched.Status && formik.errors.Status}
            />
          </div>
          <div className=" p-7">
            <AddButton
              text="Submit"
              isLoading={isSubmitting}
              type="Submit"
              disabled={isSubmitting}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditUnion;
